import React, {useEffect, useRef, useState} from 'react'
import {Alert, Button, Caption, Card, Colors, H4, H6, Icon, Info, Modal, ModalActions, P, Row, SortableList, ToolTip, Popup, ScrollView, S2, Stack} from '@components/UI/index.js'
import {formSpacing, FormStack, Select, Toggle} from '@components/UI/forms/index.js'
import {NewRouteTemplate, RouteTemplate} from 'server/functions/route/route.types.js'
import t from '../../helpers/Languages.js'
import {Customer, OrderTemplate, Reseller, User} from 'types/types.js'
import OrderActions from 'src/actions/OrderActions.js'
import moment from 'moment'
import MainFormFields from './EditRouteTemplateModal/MainFormFields.js'
import RouteCalculations from './EditRouteTemplateModal/RouteCalculations.js'
import OrderConditionGroupsEditor from '@components/conditions/OrderConditionGroupsEditor.js'
import {formatDuration} from '@utils/utils.js'
import _ from 'lodash'
import time from 'server/utils/time.js'
import DividerHeader from '@components/UI/surfaces/DividerHeader.js'
import {normalizeAdjustableByMessengerGrouped, getResellerMessengerSettings} from '../../../utils/routeAdjustability.js'
import type {PopupRef} from '../UI/overlays/Popup.js'

type EditRouteTemplateModalProps = {
    onClose: () => void
    template: RouteTemplate | NewRouteTemplate
    onChange: (template: RouteTemplate | NewRouteTemplate) => void
    onSave: (editDate?: string) => Promise<void>
    messengers: User[]
    customers: {
        [customerId: string]: Customer
    }
    reseller: Reseller
    error?: string
}

type OrderTemplateAddress = OrderTemplate['addresses'][number]

interface ConnectedOrderTemplateAddress extends OrderTemplateAddress {
    id: string
    index: number
}

type ConnectedOrderTemplateAddresses = ConnectedOrderTemplateAddress[]

function EditRouteTemplateModal({onClose, template, onChange, messengers, customers, reseller, onSave, error: parentError}: EditRouteTemplateModalProps) {
    const [orderTemplates, setOrderTemplates] = useState<OrderTemplate[]>([])
    const [error, setError] = useState<string | null>(null)
    const [orderTemplatesLoading, setOrderTemplatesLoading] = useState(true)
    const [loading, setLoading] = useState(false)
    const [connectedOrderTemplateAddresses, setConnectedOrderTemplateAddresses] = useState<ConnectedOrderTemplateAddresses>([])
    const [nameInvalid, setNameInvalid] = useState(false)
    const [pointsInvalid, setPointsInvalid] = useState(false)
    const [modalHeight, setModalHeight] = useState<number | null>(null)
    const [modalNode, setModalNode] = useState<HTMLDivElement | null>(null)
    const popupRef = useRef<PopupRef>(null)
    const originalConnectedRecurringOrdersAddressesRef = useRef<ConnectedOrderTemplateAddresses>([])
    const [recurringOrdersHasChanged, setRecurringOrdersHasChanged] = useState(false)
    const [connectOrderTemplates, setConnectOrderTemplates] = useState(template.connectedRecurringOrders.length > 0)

    useEffect(() => {
        if (!modalNode) return
        const resizeObserver = new ResizeObserver(() => {
            setModalHeight((modalNode.firstChild as HTMLDivElement).offsetHeight)
        })
        resizeObserver.observe(modalNode.firstChild as HTMLDivElement)
        return () => {
            resizeObserver.disconnect()
        }
    }, [modalNode])

    useEffect(() => {
        if ('_id' in template && template._id) {
            console.log(template)
        }

        setOrderTemplatesLoading(true)
        // get order recurring templates
        OrderActions.getTemplates({}, (err: string | null, fetchedOrderTemplates: OrderTemplate[]) => {
            if (err) {
                setError(err)
                setOrderTemplatesLoading(false)
                return
            }

            setOrderTemplates(fetchedOrderTemplates)
            setOrderTemplatesLoading(false)
        })
    }, [])

    useEffect(() => {
        if (template.endDate && template.endDate < template.startDate) {
            onChange({...template, endDate: template.startDate})
        }
    }, [template.startDate, template.endDate])

    useEffect(() => {
        const routeCalculation = template.routeCalculation || {}
        const resellerMessengerSettings = getResellerMessengerSettings(reseller.settings)
        const adjustableByMessengerGrouped = normalizeAdjustableByMessengerGrouped(routeCalculation.adjustableByMessengerGrouped)

        const normalizedRouteCalculation: NewRouteTemplate['routeCalculation'] = {
            ...routeCalculation,
            adjustableByMessengerGrouped
        }

        if (adjustableByMessengerGrouped === 'yes') {
            normalizedRouteCalculation.adjustableByMessenger = routeCalculation.adjustableByMessenger ?? resellerMessengerSettings.adjustableByMessenger
            normalizedRouteCalculation.allowAddAddress = routeCalculation.allowAddAddress ?? resellerMessengerSettings.allowAddAddress
            normalizedRouteCalculation.allowAddCollo = routeCalculation.allowAddCollo ?? resellerMessengerSettings.allowAddCollo
            normalizedRouteCalculation.messengerAddOrderWarning = routeCalculation.messengerAddOrderWarning ?? resellerMessengerSettings.messengerAddOrderWarning
            normalizedRouteCalculation.showWarningOnAddExistingOrder = routeCalculation.showWarningOnAddExistingOrder ?? resellerMessengerSettings.showWarningOnAddExistingOrder
            normalizedRouteCalculation.allowMessengerEditCustomOptions = routeCalculation.allowMessengerEditCustomOptions ?? resellerMessengerSettings.allowMessengerEditCustomOptions
        } else if (adjustableByMessengerGrouped === 'default') {
            normalizedRouteCalculation.adjustableByMessenger = routeCalculation.adjustableByMessenger ?? resellerMessengerSettings.adjustableByMessenger
            normalizedRouteCalculation.allowAddAddress = routeCalculation.allowAddAddress ?? resellerMessengerSettings.allowAddAddress
            normalizedRouteCalculation.allowAddCollo = routeCalculation.allowAddCollo ?? resellerMessengerSettings.allowAddCollo
            normalizedRouteCalculation.messengerAddOrderWarning = routeCalculation.messengerAddOrderWarning ?? resellerMessengerSettings.messengerAddOrderWarning
            normalizedRouteCalculation.showWarningOnAddExistingOrder = routeCalculation.showWarningOnAddExistingOrder ?? resellerMessengerSettings.showWarningOnAddExistingOrder
            normalizedRouteCalculation.allowMessengerEditCustomOptions = routeCalculation.allowMessengerEditCustomOptions ?? resellerMessengerSettings.allowMessengerEditCustomOptions
        } else {
            normalizedRouteCalculation.adjustableByMessenger = routeCalculation.adjustableByMessenger ?? false
            normalizedRouteCalculation.allowAddAddress = routeCalculation.allowAddAddress ?? false
            normalizedRouteCalculation.allowAddCollo = routeCalculation.allowAddCollo ?? ''
            normalizedRouteCalculation.messengerAddOrderWarning = routeCalculation.messengerAddOrderWarning ?? 'notallowed'
            normalizedRouteCalculation.showWarningOnAddExistingOrder = routeCalculation.showWarningOnAddExistingOrder ?? false
            normalizedRouteCalculation.allowMessengerEditCustomOptions = routeCalculation.allowMessengerEditCustomOptions ?? false
        }

        if (!_.isEqual(routeCalculation, normalizedRouteCalculation)) {
            onChange({...template, routeCalculation: normalizedRouteCalculation})
        }
    }, [template, reseller, onChange])

    useEffect(() => {
        const connected = template.connectedRecurringOrders
            .map((id) => orderTemplates.find((orderTemplate) => orderTemplate._id === id))
            .filter(Boolean)

        if (connected.length === 0) return

        let totalAddressIndex = -1

        let addresses = connected.flatMap((orderTemplate) => {
            return orderTemplate.addresses.map((address, index) => {
                totalAddressIndex += 1
                return {
                    ...address,
                    id: `${orderTemplate._id}_${index}`,
                    index: totalAddressIndex
                }
            })
        })

        if (template.connectedRecurringOrdersAddresses?.length > 0) {
            for (const address of template.connectedRecurringOrdersAddresses) {
                const addressIndex = addresses.findIndex((a) => a.id === address.id)
                if (addressIndex !== -1) {
                    if (typeof address.index === 'number') {
                        addresses[addressIndex].index = address.index
                    }
                }
            }
        }

        addresses.sort((a, b) => a.index - b.index)

        setConnectedOrderTemplateAddresses(addresses)
        originalConnectedRecurringOrdersAddressesRef.current = addresses
    }, [template.connectedRecurringOrders, orderTemplates])

    if (orderTemplatesLoading) {
        return null
    }

    return (
        <>
            <Modal
                show
                onClose={onClose}
                closeButton
                title={t(('_id' in template && template._id) ? 'editRouteTemplate' : 'newRouteTemplate')}
                style={{position: 'relative'}}
                ref={setModalNode}
            >
                <div>
                    {template.lockRecurringOrdersAddressesOrder && template.connectedRecurringOrders.length > 0 && (
                        <Card style={{width: 540, backgroundColor: 'white', position: 'absolute', top: 0, left: 824, height: modalHeight, display: 'flex', flexDirection: 'column', padding: 12}}>
                            <div style={{display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12}}>
                                <H4>{t('orderAddresses')}</H4>
                                <Info style={{marginLeft: 6}} color={Colors.textDark} text={t('routeTemplateAddressOrderInfo')} />
                            </div>

                            {/* <div style={{height: 300}}>
                            map
                        </div> */}

                            <div style={{overflowY: 'auto', flex: 1}}>
                                <SortableList
                                    items={connectedOrderTemplateAddresses}
                                    onChange={(addresses: ConnectedOrderTemplateAddresses) => {
                                        if ('_id' in template && template._id) {
                                            setRecurringOrdersHasChanged(true)
                                        }

                                        addresses = addresses.map((address, index) => {
                                            return {...address, index}
                                        })

                                        setConnectedOrderTemplateAddresses([...addresses])
                                    }}
                                    renderItem={(address: ConnectedOrderTemplateAddresses[number]) => {
                                        let beforeTimeSlot = false
                                        let afterTimeSlot = false

                                        if (address.estimatedTimeOfArrival) {
                                            afterTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
                                        } else if (typeof address.duration === 'number') {
                                            afterTimeSlot = moment().add(address.duration || 0, 'seconds').isAfter(moment(address.endTime, 'HH:mm'))
                                        }

                                        if (address.timeOfArrival) {
                                            afterTimeSlot = moment(address.timeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
                                        }

                                        if (!afterTimeSlot) {
                                            if (address.estimatedTimeOfArrival) {
                                                beforeTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
                                            } else if (typeof address.duration === 'number') {
                                                beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
                                            }

                                            if (address.timeOfArrival) {
                                                beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
                                            }
                                        }

                                        const inTimeSlot = !beforeTimeSlot && !afterTimeSlot

                                        let diff
                                        let formattedDiff = ''

                                        if (address.timeOfArrival && address.plannedTimeOfArrival) {
                                            diff = moment(address.timeOfArrival, 'HH:mm').diff(moment(address.plannedTimeOfArrival, 'HH:mm'), 'seconds')
                                            formattedDiff = formatDuration(diff, true)
                                        }

                                        let formattedDuration = ''

                                        if (typeof address.duration === 'number' && !address.estimatedTimeOfArrival) {
                                            formattedDuration = formatDuration(address.duration)
                                        }

                                        return (
                                            <Card
                                                shadow
                                                style={{display: 'flex', alignItems: 'center', padding: 0, marginBottom: 6, width: '100%', cursor: address.locked ? 'default' : 'grab'}}
                                            >
                                                <div
                                                    style={{
                                                        background: Colors[address.type],
                                                        width: 4,
                                                        height: 69
                                                    }}
                                                />

                                                <Stack style={{flex: 1, overflow: 'hidden', paddingLeft: 6, paddingRight: 6}}>
                                                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>

                                                        {/* <Caption ellipsis style={{flex: 1}}>{address.senderAddress?.name || address.customerAddress?.name}</Caption> */}

                                                        {!address.hasArrived &&
                                                            <Caption ellipsis>{`${address.startTime} - ${address.endTime}`}</Caption>
                                                        }

                                                        {!address.hasArrived && formattedDuration &&
                                                            <>
                                                                <Icon
                                                                    size='md'
                                                                    style={{marginRight: 6, marginLeft: 24, color: afterTimeSlot ? Colors.errorBright : undefined}}
                                                                    icon={inTimeSlot ? 'mdi mdi-timer-sand' : 'mdi mdi-timer-sand-complete'}
                                                                />
                                                                <P>{formattedDuration}</P>
                                                            </>
                                                        }

                                                        {!address.hasArrived && address.estimatedTimeOfArrival &&
                                                            <ToolTip text={t('expectedTimeOfArrival')}>
                                                                <Row style={{flexShrink: 0, marginLeft: 24}}>
                                                                    <Icon
                                                                        size='md'
                                                                        style={{marginRight: 6, color: afterTimeSlot ? Colors.errorBright : beforeTimeSlot ? Colors.warningBright : undefined}}
                                                                        icon={inTimeSlot ? 'mdi mdi-clock-outline' : 'mdi mdi-clock-alert-outline'}
                                                                    />
                                                                    <P>{address.estimatedTimeOfArrival}</P>
                                                                </Row>
                                                            </ToolTip>
                                                        }

                                                        {address.hasArrived &&
                                                            <ToolTip text={address.completedByReseller ? 'Berekende ETA. Rit afgerond door planner.' : 'Aankomsttijd'} placement='left'>
                                                                <Row style={{flexShrink: 0, marginLeft: 24}}>
                                                                    {!address.completedByReseller && (
                                                                        <Icon
                                                                            size='md'
                                                                            style={{marginRight: 6, color: afterTimeSlot ? Colors.errorBright : beforeTimeSlot ? Colors.warningBright : undefined}}
                                                                            icon={inTimeSlot ? 'mdi mdi-clock-check-outline' : 'mdi mdi-clock-alert-outline'}
                                                                        />
                                                                    )}

                                                                    <P style={address.completedByReseller ? {color: Colors.textMedium} : undefined}>{address.timeOfArrival}</P>
                                                                </Row>
                                                            </ToolTip>
                                                        }

                                                        {formattedDiff && !address.completedByReseller &&
                                                            <ToolTip text='Afwijking ETA'>
                                                                <P style={{marginLeft: 24}}>{formattedDiff}</P>
                                                            </ToolTip>
                                                        }
                                                    </Row>

                                                    <Row style={{flexShrink: 0, alignItems: 'center', height: 24}}>
                                                        {!address.position &&
                                                            <Icon style={{marginRight: 6}} size='md' icon='mdi mdi-alert' />
                                                        }

                                                        <P ellipsis style={{flex: 1}}>{`${address.street} ${address.nr}${address.addition} ${address.postalCode} ${address.city}`}</P>

                                                        {address.hasArrived &&
                                                            <Icon
                                                                size='sm'
                                                                style={{marginRight: 6, width: 24, color: address.success ? Colors.successBright : Colors.warningBright}}
                                                                icon='mdi mdi-circle'
                                                            />
                                                        }
                                                    </Row>

                                                    <Row style={{flexShrink: 0, alignItems: 'center', height: 21}}>
                                                        <Icon size='md' style={{marginRight: 6, color: Colors.textMedium}} icon={address.isBusiness ? 'mdi mdi-store-outline' : 'mdi mdi-account'} />
                                                        <Caption ellipsis style={{flex: 1}}>{address.name}</Caption>

                                                    </Row>
                                                </Stack>
                                            </Card>
                                        )
                                    }}
                                />
                            </div>
                        </Card>
                    )}

                    {error && (
                        <Alert style={{marginBottom: 6}} variant='error'>{error}</Alert>
                    )}
                    {parentError && (
                        <Alert style={{marginBottom: 6}} variant='error'>{parentError}</Alert>
                    )}

                    <MainFormFields
                        template={template}
                        onChange={onChange}
                        messengers={messengers}
                        reseller={reseller}
                        nameInvalid={nameInvalid}
                        setNameInvalid={setNameInvalid}
                    />

                    <RouteCalculations
                        template={template}
                        onChange={onChange}
                    />

                    <H6 style={{marginBottom: 12, marginTop: 36}}>{t('connectedOrderTemplates')}</H6>
                    <P>{t('connectedOrderTemplatesInfo')}</P>

                    <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                        <Toggle
                            label={t('enabled')}
                            checked={connectOrderTemplates}
                            onChange={(event) => {
                                setConnectOrderTemplates(event.target.checked)

                                // Turning it off disconnects the order templates
                                if (!event.target.checked && template.connectedRecurringOrders.length) {
                                    if ('_id' in template && template._id) {
                                        setRecurringOrdersHasChanged(true)
                                    }
                                    setConnectedOrderTemplateAddresses([])
                                    onChange({...template, connectedRecurringOrders: [], lockRecurringOrdersAddressesOrder: false})
                                }
                            }}
                        />

                        {connectOrderTemplates && (
                            <>
                                <Select
                                    label={t('orderTemplates')}
                                    values={template.connectedRecurringOrders}
                                    onChange={(event) => {
                                        if ('_id' in template && template._id) {
                                            setRecurringOrdersHasChanged(true)
                                        }

                                        // To preserve the order
                                        const prev = template.connectedRecurringOrders
                                        const next = event.target.values
                                        const added = next.filter((id) => !prev.includes(id))
                                        const updated = prev.filter((id) => next.includes(id)).concat(added)
                                        onChange({...template, connectedRecurringOrders: updated.filter((el) => typeof el === 'string')})
                                    }}

                                    options={orderTemplates.filter((orderTemplate) => !!orderTemplate.name).map((orderTemplate) => ({
                                        title: orderTemplate.name,
                                        value: orderTemplate._id
                                    }))}
                                />

                                <Toggle
                                    label={t('lockAddressesOrder')}
                                    info={t('lockAddressesOrderInfo')}
                                    checked={template.lockRecurringOrdersAddressesOrder}
                                    onChange={(event) => {
                                        if ('_id' in template && template._id) {
                                            setRecurringOrdersHasChanged(true)
                                        }
                                        onChange({...template, lockRecurringOrdersAddressesOrder: event.target.checked})
                                    }}
                                />
                            </>
                        )}
                    </FormStack>

                    <H6 style={{marginBottom: 12, marginTop: 36}}>{t('fillRouteAutomatically')}</H6>
                    <div>
                        <P style={{display: 'inline'}}>{t('fillRouteAutomaticallyInfo')}</P>
                        <div style={{display: 'inline-block', marginLeft: 6, verticalAlign: 'middle'}}>
                            <Info color={Colors.textDark} text={t('ignoreDepotsInfo')} />
                        </div>
                    </div>

                    <FormStack style={{marginTop: formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                        <Toggle
                            label={t('enabled')}
                            checked={!!template.autoFill}
                            onChange={(event) => onChange({...template, autoFill: event.target.checked})}
                        />

                        {template.autoFill &&
                            <Select
                                label={t('whenRouteIsReady')}
                                value={template.routeCalculation.whenRouteIsReady}
                                onChange={(event) => onChange({...template, routeCalculation: {...template.routeCalculation, whenRouteIsReady: event.target.value as NewRouteTemplate['routeCalculation']['whenRouteIsReady']}})}
                                options={[
                                    {
                                        title: t('doNotPutOrdersInRoute'),
                                        value: 'ignoreRest'
                                    },
                                    {
                                        title: t('addStopsToRoute'),
                                        value: 'addToRoute'
                                    },
                                    {
                                        title: t('createNewRoute'),
                                        value: 'newRoute'
                                    }
                                ]}
                            />
                        }

                    </FormStack>

                    {template.autoFill &&
                        <OrderConditionGroupsEditor
                            conditionGroups={template.conditionGroups}
                            matchAll={template.autoFillRulesMatchAll}
                            onChange={(conditionGroups, autoFillRulesMatchAll) => onChange({...template, conditionGroups: conditionGroups as NewRouteTemplate['conditionGroups'], autoFillRulesMatchAll})}
                            customers={customers}
                            reseller={reseller}
                        />
                    }

                    {'_id' in template && template._id &&
                        <DividerHeader title={t('history')} style={{marginBottom: 18, marginTop: 12}} defaultHidden>
                            <ScrollView style={{maxHeight: 250}}>
                                {[...template.history].reverse().map((entry, index) => (
                                    <Row key={`history-${index}`} style={{alignItems: 'center', flexShrink: 0}}>
                                        <div style={{height: 10, width: 10, borderRadius: '50%', marginRight: 15, background: Colors.grey20}} />

                                        <Row style={{flex: 1, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}>
                                            {entry.description ?
                                                (
                                                    <P style={{whiteSpace: 'pre'}}>{entry.description}</P>
                                                ) :
                                                entry.type === 'create' ?
                                                    (
                                                        <P>{t('templateCreated')}</P>
                                                    ) :
                                                    entry.type === 'update' ?
                                                        (
                                                            <P>{t('templateUpdated')}</P>
                                                        ) :
                                                        null}
                                        </Row>

                                        <Row style={{paddingLeft: 6, paddingRight: 6, width: 200, minWidth: 'auto'}}>
                                            <S2>{`${entry.who} | ${entry.timestamp ? moment(entry.timestamp).format('DD-MM-YYYY HH:mm:ss') : undefined}`}</S2>
                                        </Row>
                                    </Row>
                                ))}
                            </ScrollView>
                        </DividerHeader>
                    }

                    {nameInvalid && (
                        <Alert style={{marginBottom: 6, marginTop: 12}} variant='warning'>
                            Vul een naam in
                        </Alert>
                    )}

                    {pointsInvalid && (
                        <Alert style={{marginTop: 12}} variant='warning'>
                            {t('fillInStartAndEndPoint')}
                        </Alert>
                    )}

                    <ModalActions style={{marginTop: 24}}>
                        <Button onClick={onClose} variant='outline-white'>
                            {t('cancel')}
                        </Button>
                        <Button
                            onClick={async () => {
                                const pointsMissing = !template.startPoint?.position?.lat || !template.endPoint?.position?.lat
                                setPointsInvalid(pointsMissing)
                                if (pointsMissing) return

                                if (template.name) {
                                    let date: string | undefined

                                    const save = async () => {
                                        setLoading(true)

                                        if (!('_id' in template) || !template._id) {
                                            template.connectedRecurringOrdersAddresses = connectedOrderTemplateAddresses
                                        } else {
                                            // Check if connectedOrderTemplateAddresses have changed
                                            const isSame = _.isEqual(connectedOrderTemplateAddresses, originalConnectedRecurringOrdersAddressesRef.current)
                                            if (!isSame) {
                                                template.connectedRecurringOrdersAddresses = connectedOrderTemplateAddresses
                                            }

                                            if (recurringOrdersHasChanged) {
                                                template.connectedRecurringOrdersAddresses = connectedOrderTemplateAddresses
                                            }
                                        }

                                        try {
                                            popupRef.current?.close()

                                            await onSave(date)
                                        } finally {
                                            setLoading(false)
                                        }
                                    }

                                    if ('_id' in template && template._id) {
                                        popupRef.current?.setInput({
                                            type: 'date',
                                            value: template.startDate > time.today() ? template.startDate : time.today(),
                                            minDate: template.startDate > time.today() ? template.startDate : time.today(),
                                            maxDate: template.endDate || moment().add(12, 'months').format('YYYY-MM-DD'),
                                            label: t('date')
                                        })

                                        date = await popupRef.current?.open(t('editRouteTemplate'), t('editRouteTemplateFromDateInfo'))

                                        if (date === undefined) return

                                        popupRef.current?.setLoading(true)
                                    }

                                    save()
                                } else {
                                    setNameInvalid(true)
                                }
                            }}
                            loading={loading}
                        >
                            {t('save')}
                        </Button>
                    </ModalActions>
                </div>
            </Modal>
            <Popup ref={popupRef} />
        </>
    )
}

export default EditRouteTemplateModal
